import { cn } from "@/lib/cn";

interface Props {
  rollup: { total: number; done: number } | null | undefined;
}

/**
 * Compact Jira "done / total" pill for the Portfolio table. Color
 * indicates progress at a glance (sitewide rule):
 *   - emerald: every linked issue is done
 *   - sky:     some done, some open (in progress)
 *   - slate:   none done yet (not in progress)
 *
 * Returns an em-dash for projects that have no Jira links — keeps the
 * column quiet for non-IT projects rather than implying 0/0 progress.
 */
export function JiraRollupBadge({ rollup }: Props) {
  if (!rollup || rollup.total === 0) {
    return <span className="text-xs text-ink-faint">{"\u2014"}</span>;
  }
  const { total, done } = rollup;
  const allDone = done === total;
  const someDone = done > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
        allDone
          ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30"
          : someDone
            ? "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-500/30"
            : "bg-raised text-ink-3 ring-1 ring-line",
      )}
      title={`${done} of ${total} Jira issue${total === 1 ? "" : "s"} done`}
    >
      {done}/{total}
      {allDone && <span aria-hidden>✓</span>}
    </span>
  );
}
